import React, { useEffect, useState, useMemo, useContext, useCallback } from "react";
import "./CustomizedTable.css";
import PropTypes from "prop-types";
import { Box, Table, TableBody, TableContainer, TableHead, TableRow, styled } from "@mui/material";
import { useTable, useSortBy, useFilters, useGlobalFilter, useBlockLayout, useAsyncDebounce, useRowSelect } from "react-table";
import { matchSorter } from "match-sorter";
import { VariableSizeGrid as Grid } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import DefaultColumnFilter from "./Filters/DefaultColumnFilter/DefaultColumnFilter";
import CustomizedTableHeaderCell from "./CustomizedTableHeaderCell/CustomizedTableHeaderCell";
import CustomizedTableBodyCell from "./CustomizedTableBodyCell/CustomizedTableBodyCell";
import useWindowDimensions from "../../hooks/useWindowDimensions/useWindowDimensions";
import { getSumOfArray } from "../../utils/calcHelpers";
import { PanelSizeContext } from "../Dashboard/Dashboard";

const StyledTable = styled(Table)(() => ({
  display: "inline-block",
  borderSpacing: "0",
  minWidth: 0,
  overflowY: "hidden"
}));

export default function CustomizedTable({
  id,
  columns,
  data,
  updateData,
  defaultHeight,
  defaultWidth,
  tableSearch,
  enableRowSelect,
  setSelectAll,
  hiddenColumns,
  actionCallback,
}) {
  const { height, width } = useWindowDimensions(defaultHeight, defaultWidth);
  const { sidebarWidth } = useContext(PanelSizeContext);
  const [bodyHeight, setBodyHeight] = useState(0);
  const [bodyWidth, setBodyWidth] = useState(0);
  const [tablePaddingLeft, setTablePaddingLeft] = useState(24);
  const [tablePaddingRight, setTablePaddingRight] = useState(24);
  const cellPaddingRight = 4;
  const rowHeight = 32;
  const defaultColumnWidth = 150;
  const carrierMinWidth = 90;
  const customerMinWidth = 110;
  const gridRef = React.createRef();

  const excludedPaths = [
    "freight-matching",
    "quick-actions",
    "load-booking",
    "carrier-offer",
    "bounce-carrier",
    "carrier-capacity",
    "carrier-contact",
    "load-contact",
    "load-profile",
    "carrier-profile",
    "carrier-search",
    "offer-boards"
  ];

  const windowWidth = !excludedPaths.some(e => window.location.pathname.includes(e)) ? width - sidebarWidth : width - 20;

  useEffect(() => {
    if (tableSearch) {
      handleTableSearch(tableSearch);
    } else {
      setGlobalFilter("");
    }
  }, [tableSearch, data]);

  const handleTableSearch = useAsyncDebounce(tableSearch => {
    setGlobalFilter(tableSearch || undefined);
  }, 1);

  const fuzzyTextFilterFn = useCallback((rows, id, filterValue) => {
    return matchSorter(rows, filterValue, { keys: [row => row.values[id]] });
  }, []);

  fuzzyTextFilterFn.autoRemove = val => !val;

  const dateBetweenFilterFn = useCallback((rows, id, filterValues) => {
    let sd = new Date(filterValues[0]);
    let ed = new Date(filterValues[1]);
    return rows.filter(r => {
      const time = new Date(r.values[id]);
      if (filterValues.length === 0) return rows;
      return (time >= sd && time <= ed);
    });
  }, []);

  dateBetweenFilterFn.autoRemove = val => !val;

  const multiFilterFn = useCallback((rows, id, filterValues) => {
    return rows.filter(row => row.values[id] ? filterValues.includes(row.values[id].trim().toUpperCase()) : false);
  }, []);

  multiFilterFn.autoRemove = val => !val;

  const multiNumericFilterFn = useCallback((rows, id, filterValues) => {
    return rows.filter(row => row.values[id] ? filterValues.includes(row.values[id]) : false);
  }, []);

  multiNumericFilterFn.autoRemove = val => !val;

  const filterTypes = useMemo(
    () => ({
      fuzzyText: fuzzyTextFilterFn,
      dateBetween: dateBetweenFilterFn,
      multi: multiFilterFn,
      multiNumeric: multiNumericFilterFn,
      text: (rows, id, filterValue) => {
        return rows.filter(row => {
          const rowValue = row.values[id];
          return rowValue !== undefined
            ? String(rowValue)
              .toLowerCase()
              .startsWith(String(filterValue).toLowerCase())
            : true;
        });
      },
    }),
    []
  );

  const initialState = useMemo(() => ({
    hiddenColumns: hiddenColumns ?? []
  }), [hiddenColumns]);

  const defaultColumn = useMemo(
    () => ({
      Filter: DefaultColumnFilter,
      width: defaultColumnWidth
    }),
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    setGlobalFilter,
  } = useTable(
    {
      id,
      columns,
      data,
      defaultColumn,
      filterTypes,
      updateData,
      setSelectAll,
      autoResetSortBy: false,
      autoResetFilters: false,
      initialState,
      actionCallback
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    useBlockLayout,
    useRowSelect
  );

  const columnWidths = useMemo(() => {
    const widths = [];

    if (headerGroups[0]) {
      headerGroups[0].headers.forEach(header => {
        if (header.Header === "Carrier") {
          let temp = data.map(({ carrierName, name }) => carrierName || name).sort((a, b) => b.length - a.length);
          let width = temp.length && temp[0] != undefined ? (temp[0].length * 9) + 80 : 0;
          widths.push(width > carrierMinWidth && width < header.width ? width : width < carrierMinWidth ? carrierMinWidth : header.width);
        } else if (header.Header === "Customer") {
          let temp = data.map(({ customerName }) => customerName).sort((a, b) => b.length - a.length);
          let width = temp.length && temp[0] != undefined ? temp[0].length * 7 : 0;
          widths.push(width > customerMinWidth && width < header.width ? width : width < customerMinWidth ? customerMinWidth : header.width);
        } else {
          widths.push(header.width);
        }
      });
    }

    return widths;
  }, [headerGroups, data]);

  const totalWidth = useMemo(() => getSumOfArray(columnWidths), [columnWidths]);

  const getCellWidth = useCallback(() => {
    return totalWidth;
  }, [totalWidth]);

  const lastColumnWidth = useCallback((index) => {
    const remainingWidth = windowWidth - getCellWidth() + columnWidths[index] - cellPaddingRight - tablePaddingLeft - tablePaddingRight;

    if (defaultWidth) {
      return width - getCellWidth() + columnWidths[index];
    } else if (remainingWidth < columnWidths[index]) {
      return columnWidths[index];
    } else {
      return remainingWidth;
    }
  }, [defaultWidth, width, columnWidths, windowWidth, cellPaddingRight, tablePaddingLeft, tablePaddingRight, getCellWidth]);

  const getColumnWidth = useCallback((index) => index === columns.length - 1 ? lastColumnWidth(index) : columnWidths[index] || defaultColumnWidth, [columnWidths]);

  useEffect(() => {
    if (gridRef.current != null) {
      gridRef.current.resetAfterIndices({
        columnIndex: 0,
        shouldForceUpdate: false
      });
    }
  }, [columnWidths]);

  useEffect(() => {
    const div = document.getElementById(id);
    if (div && defaultHeight) {
      const adjustedHeight = height - bodyWidth > 0 ? height - bodyWidth : height;
      setBodyHeight(adjustedHeight);
    } else if (div && !defaultHeight) {
      setBodyHeight(height - div.getBoundingClientRect().top - 15);
    }
  }, [columns, data, height, width, id]);

  useEffect(() => {
    const cellWidth = getCellWidth();

    if (defaultWidth) {
      setBodyWidth(width);
      setTablePaddingLeft(0);
      setTablePaddingRight(0);
    } else if (cellWidth >= windowWidth) {
      setBodyWidth(cellWidth);
    } else {
      setBodyWidth(windowWidth - tablePaddingLeft - tablePaddingRight - cellPaddingRight);
    }
  }, [defaultWidth, width, sidebarWidth, totalWidth]);

  const getRowColor = useCallback((index, newlyAdded, active) => {
    if (active) {
      return "primary.70";
    }

    if (newlyAdded) {
      return "success.20";
    }

    if (index % 2 === 0) {
      return "black_opacity.10";
    }
  }, []);

  const RenderCell = useCallback(({ columnIndex, rowIndex, isScrolling, style }) => {
    const row = rows[rowIndex];
    const activeRow = row.original.activeRow;
    const newlyAdded = row.original.newlyAdded;
    prepareRow(row);
    const cell = row.cells[columnIndex];

    if (!cell) return null;

    return (
      <CustomizedTableBodyCell
        key={`cell-${rowIndex}`}
        style={style}
        backgroundColor={getRowColor(rowIndex, newlyAdded, activeRow)}
        index={rowIndex}
        cell={cell}
        isScrolling={isScrolling}
        onClick={e => {
          e.stopPropagation();
          enableRowSelect && updateData(rowIndex, "activeRow", !activeRow);
        }}
      />
    );
  }, [prepareRow, rows]);

  return (
    <div>
      <Box style={{ paddingLeft: tablePaddingLeft, paddingRight: tablePaddingRight }}>
        <TableContainer id="table-container">
          <StyledTable component="div" stickyHeader aria-label="customized table" {...getTableProps()}>
            <TableHead component="div">
              {headerGroups.map((headerGroup, i) => (
                <TableRow component="div" key={`headerGroup-${i}`} {...headerGroup.getHeaderGroupProps()}
                  sx={{ width: `${bodyWidth}px !important` }}
                >
                  {headerGroup.headers.map((column, i) => (
                    <CustomizedTableHeaderCell
                      key={`column-${i}`}
                      index={i}
                      column={column}
                      columnWidths={columnWidths}
                      lastColumnWidth={lastColumnWidth}
                    />
                  ))}
                </TableRow>
              ))}
            </TableHead>
            <TableBody id={id} component="div" sx={{ height: bodyHeight, width: bodyWidth }} {...getTableBodyProps()}>
              <AutoSizer disableWidth>
                {({ height }) => (
                  <Grid
                    ref={gridRef}
                    className="table-list"
                    columnWidth={getColumnWidth}
                    columnCount={columns.length}
                    rowHeight={() => rowHeight}
                    rowCount={rows.length}
                    width={bodyWidth}
                    height={height}
                    useIsScrolling
                  >
                    {RenderCell}
                  </Grid>
                )}
              </AutoSizer>
            </TableBody>
          </StyledTable>
        </TableContainer>
      </Box>
    </div>
  );
}

CustomizedTable.propTypes = {
  id: PropTypes.string.isRequired,
  columns: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  updateData: PropTypes.func,
  defaultHeight: PropTypes.number,
  defaultWidth: PropTypes.number,
  tableSearch: PropTypes.string,
  enableRowSelect: PropTypes.bool,
  setSelectAll: PropTypes.func,
  hiddenColumns: PropTypes.array,
  actionCallback: PropTypes.func
};